import 'react-phone-number-input/style.css'
import './style.css' // Force input styling

import { AVAILABLE_LANGUAGES, CountryCode, PhoneNumberComponent, STEP_TYPE } from '@abiglobalhealth/go-types'
import { FocusEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PhoneInput, { isValidPhoneNumber } from 'react-phone-number-input'
import en from 'react-phone-number-input/locale/en.json'
import es from 'react-phone-number-input/locale/es.json'

import { useFormErrors } from '@/hooks/useFormErrors'
import { useStep } from '@/hooks/useStep'
import { useUser } from '@/hooks/useUser'
import { useUserForm } from '@/hooks/useUserForm'
import i18n from '@/locale/i18n'

export type PhoneNumberProps = Omit<PhoneNumberComponent, 'componentType'> & {
  error?: string
  value?: string
  tabIndex: number
  containerClassName?: string
}

export const PhoneNumber = ({ id, textKey, required, placeholder, error, tabIndex, defaultCountry, containerClassName }: PhoneNumberProps) => {
  const { t } = useTranslation()
  const { user } = useUser()
  const { addData, getUserDataById } = useUserForm()
  const { currentStep } = useStep()
  const { formErrors, clearFormError, addFormError } = useFormErrors()

  const [value, setValue] = useState<string | null>(getUserDataById(id) as string | null)

  const handleValidation = (event: FocusEvent<HTMLInputElement, Element>) => {
    if (required && !event.target.value) {
      return addFormError(id, 'error.validation.required')
    }

    if (!isValidPhoneNumber(event.target.value)) {
      return addFormError(id, 'error.validation.phone')
    }
  }

  useEffect(() => {
    if (value === null) return

    if ([STEP_TYPE.USER_FORM, STEP_TYPE.CONSULTATION].includes(currentStep?.type as STEP_TYPE)) {
      return addData(id, value)
    }
  }, [value])

  const err = error || formErrors?.[id]

  const userCountry = user?.data.country?.code.toUpperCase() as CountryCode | undefined

  return (
    <div className={`flex w-full flex-col gap-1 ${containerClassName}`} data-testid="phone-number-container">
      <label htmlFor={id} className="font-bold">
        {t(textKey)} {required && '*'}
      </label>

      <PhoneInput
        id={id}
        name={id}
        data-testid={id}
        tabIndex={tabIndex}
        className={`rounded-lg border bg-white p-2 transition-[border-color] ${err ? 'border-error' : 'border-gray'}`}
        defaultCountry={userCountry || defaultCountry || 'US'}
        placeholder={placeholder || '+XXX XXX XXX XXX'}
        onChange={(value) => setValue(value ?? null)}
        onBlur={handleValidation}
        onFocus={() => clearFormError(id)}
        value={value ?? undefined}
        international
        autoComplete="none"
        labels={i18n.language === AVAILABLE_LANGUAGES.ES_MX ? es : en}
      />

      <span data-testid="phone-number-error-span" className="animate-fade-in text-error min-h-[1.5rem] text-sm">
        {err && t(err)}
      </span>
    </div>
  )
}
